import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch, Typography } from "@mui/material";

import {
  AcceptAllButton,
  AlwaysActiveLabel,
  BannerBody,
  BannerContainer,
  BannerDescription,
  BannerHeader,
  BannerOverlay,
  ButtonGroup,
  CategoryDescription,
  CategoryInfo,
  CategoryRow,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  IconCircle,
  LinkDivider,
  LinksRow,
  ManagePreferencesButton,
  PolicyLink,
  PreferencesDialog,
  RejectAllButton,
  SavePreferencesButton,
  StyledCloseIcon,
  StyledCookieIcon,
  StyledSettingsIcon,
} from "./styles";
import { UserContext } from "@/context/UserContext";
import { useSnackbar } from "@/hooks/useSnackbar";
import { fetchMyUser, updateUserStatesBulk } from "@/lib/api";

type GdprConsentModalProps = {
  open: boolean;
};

const GdprConsentModal = ({ open }: GdprConsentModalProps) => {
  const { t } = useTranslation();
  const { user, setUser } = useContext(UserContext);
  const { showSnackbar } = useSnackbar();
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [experienceConsent, setExperienceConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const seedConsent = () => {
    setAnalyticsConsent(Boolean(user?.states?.gdprConsentAnalytics));
    setExperienceConsent(Boolean(user?.states?.gdprConsentExperience));
  };

  const openPreferences = () => {
    seedConsent();
    setShowPreferences(true);
  };

  const closePreferences = () => {
    seedConsent();
    setShowPreferences(false);
  };

  const saveConsent = async (analytics: boolean, experience: boolean) => {
    if (!user) return;
    setSaving(true);
    try {
      await updateUserStatesBulk(String(user.id), {
        gdprConsentAccepted: true,
        gdprConsentAnalytics: analytics,
        gdprConsentExperience: experience,
      });
      const updatedUser = await fetchMyUser();
      setUser(updatedUser);
    } catch (error) {
      console.error("Error saving GDPR consent:", error);
      showSnackbar(t("gdpr.consentModal.saveError"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptAll = () => saveConsent(true, true);
  const handleRejectAll = () => saveConsent(false, false);
  const handleSavePreferences = () =>
    saveConsent(analyticsConsent, experienceConsent);

  if (!open) return null;

  return (
    <>
      {!showPreferences && (
        <BannerOverlay>
          <BannerContainer>
            <BannerHeader>
              <IconCircle>
                <StyledCookieIcon />
              </IconCircle>
              <Typography variant="subtitle1">
                {t("gdpr.consentModal.title")}
              </Typography>
            </BannerHeader>

            <BannerDescription variant="body2">
              {t("gdpr.consentModal.description")}
            </BannerDescription>

            <BannerBody>
              <ButtonGroup>
                <AcceptAllButton onClick={handleAcceptAll} disabled={saving}>
                  {t("gdpr.consentModal.acceptAll")}
                </AcceptAllButton>
                <RejectAllButton onClick={handleRejectAll} disabled={saving}>
                  {t("gdpr.consentModal.rejectAll")}
                </RejectAllButton>
                <ManagePreferencesButton onClick={openPreferences}>
                  <StyledSettingsIcon />
                  {t("gdpr.consentModal.managePreferences")}
                </ManagePreferencesButton>
              </ButtonGroup>

              <LinksRow>
                <PolicyLink
                  href="https://bx.tech/cookies/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("gdpr.consentModal.cookiePolicyLink")}
                </PolicyLink>
                <LinkDivider>&middot;</LinkDivider>
                <PolicyLink
                  href="https://bx.tech/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("gdpr.consentModal.privacyPolicyLink")}
                </PolicyLink>
              </LinksRow>
            </BannerBody>
          </BannerContainer>
        </BannerOverlay>
      )}

      <PreferencesDialog open={showPreferences} onClose={closePreferences}>
        <DialogHeader>
          <Typography variant="subtitle1">
            {t("gdpr.consentModal.preferencesTitle")}
          </Typography>
          <DialogCloseButton onClick={closePreferences}>
            <StyledCloseIcon />
          </DialogCloseButton>
        </DialogHeader>

        <DialogBody>
          <CategoryRow>
            <CategoryInfo>
              <Typography variant="subtitle2">
                {t("gdpr.consentModal.categories.essential.title")}
              </Typography>
              <CategoryDescription>
                {t("gdpr.consentModal.categories.essential.description")}
              </CategoryDescription>
            </CategoryInfo>
            <AlwaysActiveLabel>
              {t("gdpr.consentModal.alwaysActive")}
            </AlwaysActiveLabel>
          </CategoryRow>

          <CategoryRow>
            <CategoryInfo>
              <Typography variant="subtitle2">
                {t("gdpr.consentModal.categories.analytics.title")}
              </Typography>
              <CategoryDescription>
                {t("gdpr.consentModal.categories.analytics.description")}
              </CategoryDescription>
            </CategoryInfo>
            <Switch
              checked={analyticsConsent}
              onChange={(e) => setAnalyticsConsent(e.target.checked)}
              size="small"
              inputProps={{
                "aria-label": t("gdpr.consentModal.categories.analytics.title"),
              }}
            />
          </CategoryRow>

          <CategoryRow>
            <CategoryInfo>
              <Typography variant="subtitle2">
                {t("gdpr.consentModal.categories.experience.title")}
              </Typography>
              <CategoryDescription>
                {t("gdpr.consentModal.categories.experience.description")}
              </CategoryDescription>
            </CategoryInfo>
            <Switch
              checked={experienceConsent}
              onChange={(e) => setExperienceConsent(e.target.checked)}
              size="small"
              inputProps={{
                "aria-label": t(
                  "gdpr.consentModal.categories.experience.title"
                ),
              }}
            />
          </CategoryRow>
        </DialogBody>

        <DialogFooter>
          <SavePreferencesButton
            onClick={handleSavePreferences}
            disabled={saving}
          >
            {t("gdpr.consentModal.acceptButton")}
          </SavePreferencesButton>
        </DialogFooter>
      </PreferencesDialog>
    </>
  );
};

export default GdprConsentModal;
